import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {Button, FormControl, Textarea, SkeletonBox, Banner} from '@primer/react'
import {InlineMessage} from '@primer/react/experimental'
import {useCallback, useRef, useState, forwardRef, useImperativeHandle, useMemo, useId} from 'react'
import {clsx} from 'clsx'
import {COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER, COPILOT_INSTRUCTIONS_MAX_LENGTH} from '../../utils/constants'
import {
  useCopilotRepositoryFetching,
  type DropdownRepository,
  type SimpleRepository,
} from '../../hooks/useCopilotRepoFetching'
import {useDefaultOwnerAwareRepositoryLoader} from '../../hooks/useDefaultOwnerAwareRepositoryLoader'
import styles from './CopilotAssignmentForm.module.css'
import {CommandButton} from '@github-ui/ui-commands'
import {SubAgentSelector} from '../new-task-chat-input/SubAgentSelector'
import {isFeatureEnabled} from '@github-ui/feature-flags'

const CROSS_ORG_WARNING_TEXT =
  "You're assigning Copilot coding agent to open a pull request in a different organization. The issue description will be visible to users outside this repository."
const PRIVATE_TO_PUBLIC_WARNING_TEXT =
  "You're assigning Copilot coding agent to open a pull request in a public repository. The issue description will be visible to users outside this repository."
const EMPTY_REPO_WARNING_TEXT =
  "The selected repo has no commits or branches so there's no valid base branch to create a PR against."

export interface CopilotAssignmentFormRef {
  submit: () => Promise<void>
}

export interface CopilotAssignmentFormProps {
  initialRepo: SimpleRepository
  onSubmit: (
    content: string,
    selectedRepo: DropdownRepository | null,
    selectedBranch: string,
    selectedSubAgent: string | null | undefined,
  ) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  textareaRef?: React.RefObject<HTMLTextAreaElement>
  textareaAriaDescribedBy?: string
  nested?: boolean
}

export const CopilotAssignmentForm = forwardRef<CopilotAssignmentFormRef, CopilotAssignmentFormProps>(
  function CopilotAssignmentForm(
    {
      initialRepo,
      onSubmit,
      onCancel,
      isSubmitting = false,
      textareaRef,
      textareaAriaDescribedBy,
    }: CopilotAssignmentFormProps,
    ref,
  ) {
    const [content, setContent] = useState('')
    const [textareaValidationError, setTextareaValidationError] = useState<string | undefined>(undefined)
    const [branchValidationError, setBranchValidationError] = useState<string | undefined>(undefined)
    const [selectedSubAgent, setSelectedSubAgent] = useState<string | null>(null)

    // Generate a unique cache key per modal mount to prevent stale branch data
    const cacheKey = `copilot-assignment-ref-selector-${useId()}`

    const internalTextareaRef = useRef<HTMLTextAreaElement>(null)
    const finalTextareaRef = textareaRef || internalTextareaRef

    // Create defaultOwner-aware repository loader that ensures initial repo is always fetched
    const initialRepoNameWithOwner = `${initialRepo.owner.login}/${initialRepo.name}`

    const defaultOwnerAwareRepositoryLoader = useDefaultOwnerAwareRepositoryLoader(
      initialRepo.owner.login,
      initialRepoNameWithOwner,
    )

    // Use the shared repository fetching hook
    const {
      selectedRepo,
      selectedBranch,
      setSelectedBranch,
      isLoadingBranch,
      initialRepoApiMetadata,
      handleRepoSelect: handleRepoSelectFromHook,
      handleReposLoaded,
    } = useCopilotRepositoryFetching(initialRepo)

    const isSweSubagentsEnabled = isFeatureEnabled('copilot_swe_agent_use_subagents')

    const warningMessage = useMemo(() => {
      if (!selectedRepo) return null

      const targetIsPublic = !selectedRepo.private
      const differentOrganization = initialRepo.owner.login !== selectedRepo.owner

      if (selectedRepo.isEmpty) {
        return EMPTY_REPO_WARNING_TEXT
      }

      if (differentOrganization) {
        return CROSS_ORG_WARNING_TEXT
      }

      if (initialRepoApiMetadata?.private && targetIsPublic) {
        return PRIVATE_TO_PUBLIC_WARNING_TEXT
      }

      return null
    }, [selectedRepo, initialRepo.owner.login, initialRepoApiMetadata])

    // Wrapper to prevent selection during submission
    const handleRepoSelect = useCallback(
      (repo: DropdownRepository | undefined) => {
        if (!repo || isSubmitting) return
        handleRepoSelectFromHook(repo)
        setBranchValidationError(undefined)
        setSelectedSubAgent(null)
      },
      [isSubmitting, handleRepoSelectFromHook],
    )

    const handleSubmit = useCallback(async () => {
      if (isSubmitting) return

      setTextareaValidationError(undefined)
      setBranchValidationError(undefined)

      // Validate character limit
      if (content.length > COPILOT_INSTRUCTIONS_MAX_LENGTH) {
        const errorMessage = `Instructions must be ${COPILOT_INSTRUCTIONS_MAX_LENGTH.toLocaleString()} characters or fewer. Current length: ${content.length.toLocaleString()} characters.`
        setTextareaValidationError(errorMessage)
        return
      }

      if (!selectedRepo || !selectedBranch) {
        setBranchValidationError('Please select a repository and branch.')
        return
      }

      if (selectedRepo.isEmpty) {
        setBranchValidationError('Please select a valid base branch.')
        return
      }

      await onSubmit(content, selectedRepo, selectedBranch, selectedSubAgent)
    }, [content, selectedRepo, selectedBranch, selectedSubAgent, onSubmit, isSubmitting])

    // Expose submit method through ref
    useImperativeHandle(
      ref,
      () => ({
        submit: handleSubmit,
      }),
      [handleSubmit],
    )

    const submitButtonInactive = selectedRepo?.isEmpty || isSubmitting || !selectedRepo || !selectedBranch
    const refSelectorInactive = isSubmitting || isLoadingBranch || !selectedRepo?.defaultBranch || selectedRepo?.isEmpty
    const customAgentPickerInactive = isSubmitting || selectedRepo?.isEmpty || !selectedBranch

    return (
      <div className={styles.formContainer}>
        <div className={styles.selectorsContainer}>
          <div className={styles.selectorsRow}>
            <FormControl className={clsx(styles.formControl, styles.justifyStart)}>
              <FormControl.Label>Target repository</FormControl.Label>
              <ReposSelector
                repositoryLoader={defaultOwnerAwareRepositoryLoader}
                selectionVariant="single"
                selectAllOption={false}
                onSelect={handleRepoSelect}
                onLoad={handleReposLoaded}
                currentSelection={selectedRepo || undefined}
                buttonText={selectedRepo?.nameWithOwner || 'Select repository'}
                width="large"
                truncateMaxWidth={250}
                additionalButtonProps={{
                  'aria-label': `Repository: ${selectedRepo?.nameWithOwner || 'Select repository'}`,
                  disabled: isSubmitting,
                }}
              />
            </FormControl>

            <FormControl className={clsx(styles.formControl, styles.justifyUnset)}>
              <FormControl.Label>Base branch</FormControl.Label>
              {selectedRepo || selectedBranch ? (
                <RefSelector
                  useFocusZone
                  cacheKey={cacheKey}
                  canCreate={false}
                  currentCommitish={selectedRepo?.isEmpty ? 'None available' : selectedBranch || 'Loading...'}
                  defaultBranch={selectedRepo?.defaultBranch ?? selectedBranch}
                  owner={selectedRepo?.owner || ''}
                  repo={selectedRepo?.nameOnly || ''}
                  types={['branch']}
                  selectedRefType="branch"
                  closeOnSelect
                  hideShowAll
                  disabled={refSelectorInactive}
                  onSelectItem={selectedRef => {
                    if (!isSubmitting) {
                      setSelectedBranch(selectedRef)
                    }
                  }}
                  anchoredOverlayProps={{
                    variant: {regular: 'anchored', narrow: 'fullscreen'},
                    height: 'small',
                  }}
                />
              ) : (
                <SkeletonBox height="32px" width="100px" />
              )}
            </FormControl>
            {isSweSubagentsEnabled && selectedRepo && (
              <FormControl className={clsx(styles.formControl, styles.justifyStart)}>
                <FormControl.Label>Custom agent</FormControl.Label>
                <SubAgentSelector
                  repo={selectedRepo}
                  branch={selectedRepo.defaultBranch}
                  selectedAgent={selectedSubAgent}
                  onSelect={setSelectedSubAgent}
                  iconOnly={false}
                  disabled={customAgentPickerInactive}
                />
              </FormControl>
            )}
          </div>

          {warningMessage && (
            <Banner variant="warning" title="Warning" hideTitle className={styles.warningBanner}>
              {warningMessage}
            </Banner>
          )}
        </div>

        <div className={styles.textareaContainer}>
          <FormControl disabled={isSubmitting}>
            <FormControl.Label>Optional prompt</FormControl.Label>
            <Textarea
              ref={finalTextareaRef}
              className={styles.textarea}
              placeholder={COPILOT_ASSIGNMENT_PROMPT_PLACEHOLDER}
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={2}
              resize="vertical"
              aria-describedby={textareaAriaDescribedBy}
              maxLength={COPILOT_INSTRUCTIONS_MAX_LENGTH}
            />
            {textareaValidationError && (
              <FormControl.Validation variant="error">{textareaValidationError}</FormControl.Validation>
            )}
          </FormControl>
        </div>

        {branchValidationError && (
          <InlineMessage variant="critical" className={styles.branchValidation}>
            {branchValidationError}
          </InlineMessage>
        )}

        <div className={styles.buttonsContainer}>
          <div className={styles.buttonsRow}>
            {onCancel && (
              <Button variant="invisible" onClick={onCancel} disabled={isSubmitting}>
                Cancel
              </Button>
            )}
            <CommandButton
              commandId="github:submit-form"
              showKeybindingHint
              variant="primary"
              inactive={submitButtonInactive}
              loading={isSubmitting}
              className={styles.assignButton}
            >
              Assign
            </CommandButton>
          </div>
        </div>
      </div>
    )
  },
)

try{ CopilotAssignmentForm.displayName ||= 'CopilotAssignmentForm' } catch {}
try{ CopilotAssignmentForm.displayName ||= 'CopilotAssignmentForm' } catch {}